import React, { useEffect, useMemo, useState } from 'react';
import { X, RefreshCw, CopyPlus } from 'lucide-react';
import { expenseAPI } from '../../services/api';
import { useToast } from '../UIProvider';
import useDialog from '../../hooks/useDialog';
import DatePicker from '../DatePicker';
import Select from '../Select';
import { groupDigits, handleAmountInput } from '../../utils/money';
import {
  DepartmentPills, formatCurrency, formatDate, todayISO, errorText, inputClass, labelClass, paymentLabel,
} from './shared';

// The salaries, the internet, the cleaning: the same lines every month. Pick the department and a
// month that was entered, tick what repeats, correct any amount, and every ticked line is saved on
// one date in one go. A line already in the ledger on that date is skipped and named, so copying a
// month twice adds nothing. Cash lines only: a cheque or transfer is a new payment with a new
// reference, and belongs in the Add expense form.

const CopyMonthModal = ({ cats, initialDepartment, onClose, onSaved }) => {
  const showToast = useToast();
  const [department, setDepartment] = useState(initialDepartment && initialDepartment !== 'ALL' ? initialDepartment : '');
  const [categoryId, setCategoryId] = useState('');
  const [months, setMonths] = useState([]);
  const [month, setMonth] = useState('');
  const [rows, setRows] = useState([]);
  const [loadingRows, setLoadingRows] = useState(false);
  const [targetDate, setTargetDate] = useState(todayISO());
  const [saving, setSaving] = useState(false);
  const dialogRef = useDialog(onClose, { isDirty: () => rows.some(r => r.checked) });

  const roots = cats.roots;
  const children = useMemo(() => cats.childrenOf(department), [cats, department]);
  const scopeId = categoryId || department;

  useEffect(() => {
    if (!department && roots.length === 1) setDepartment(roots[0].id);
  }, [roots, department]);

  // The months that actually hold expenses under the scope, newest first.
  useEffect(() => {
    let alive = true;
    setMonth('');
    setRows([]);
    if (!scopeId) { setMonths([]); return undefined; }
    expenseAPI.getMonths(scopeId)
      .then(list => {
        if (!alive) return;
        const m = Array.isArray(list) ? list : [];
        setMonths(m);
        if (m.length) setMonth(m[0].month);
      })
      .catch(() => { if (alive) setMonths([]); });
    return () => { alive = false; };
  }, [scopeId]);

  // The month's expenses, ready to tick. Cash lines ticked, the rest listed but off.
  useEffect(() => {
    let alive = true;
    if (!month || !scopeId) { setRows([]); return undefined; }
    setLoadingRows(true);
    const [y, m] = month.split('-').map(Number);
    const from = `${month}-01`;
    const to = `${month}-${String(new Date(y, m, 0).getDate()).padStart(2, '0')}`;
    expenseAPI.getEntries({ from, to, type: 'EXPENSE', categoryId: scopeId })
      .then(list => {
        if (!alive) return;
        const entries = Array.isArray(list) ? list : [];
        setRows(entries
          .slice()
          .sort((a, b) => (a.description || '').localeCompare(b.description || ''))
          .map(e => ({
            key: e.id,
            description: e.description,
            amount: String(Math.round(Number(e.amount || 0)) || ''),
            categoryId: e.categoryId,
            categoryPath: e.categoryPath,
            paymentMode: e.paymentMode,
            date: e.entryDate,
            notes: e.notes || '',
            checked: e.paymentMode === 'CASH',
          })));
      })
      .catch(() => { if (alive) setRows([]); })
      .finally(() => { if (alive) setLoadingRows(false); });
    return () => { alive = false; };
  }, [month, scopeId]);

  const setRow = (key, patch) => setRows(list => list.map(r => (r.key === key ? { ...r, ...patch } : r)));
  const picked = rows.filter(r => r.checked);
  const total = picked.reduce((s, r) => s + Number(r.amount || 0), 0);
  const allChecked = rows.length > 0 && picked.length === rows.length;

  const save = async () => {
    if (!targetDate) { showToast('error', 'Pick the date to save them on.'); return; }
    const bad = picked.find(r => !r.amount || Number(r.amount) <= 0);
    if (bad) { showToast('error', `Enter an amount for "${bad.description}".`); return; }
    if (picked.length === 0) { showToast('error', 'Tick at least one line.'); return; }
    setSaving(true);
    try {
      const result = await expenseAPI.createBatch(picked.map(r => ({
        entryDate: targetDate,
        description: r.description,
        amount: Number(r.amount),
        entryType: 'EXPENSE',
        categoryId: r.categoryId,
        paymentMode: 'CASH',
        notes: r.notes,
        confirmDuplicate: false,
        recordWithdrawal: false,
      })));
      const skipped = result.skipped || [];
      showToast(
        skipped.length ? 'warning' : 'success',
        `${result.created} ${result.created === 1 ? 'entry' : 'entries'} saved on ${formatDate(targetDate)}`
          + (skipped.length ? `; ${skipped.length} already there: ${skipped.map(s => s.description).join(', ')}` : ''),
      );
      onSaved(result);
    } catch (err) {
      showToast('error', errorText(err, 'Nothing was saved'));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div ref={dialogRef} className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4" onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-3xl max-h-[90vh] flex flex-col overflow-hidden" role="dialog" aria-modal="true" aria-labelledby="copy-month-title">
        <div className="flex-shrink-0 px-6 py-4 bg-gradient-to-r from-navy-800 to-navy-700 flex items-center justify-between">
          <div>
            <h2 id="copy-month-title" className="text-lg font-semibold text-white flex items-center gap-2"><CopyPlus className="w-5 h-5" /> Copy a month</h2>
            <p className="text-xs text-navy-100 mt-0.5">Repeat last month's salaries and bills in one go</p>
          </div>
          <button aria-label="Close" onClick={onClose} className="p-2 rounded-lg hover:bg-white/10 transition-colors">
            <X className="h-5 w-5 text-white" />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto p-6 space-y-5">
          <div>
            <label className={labelClass}>Department</label>
            <DepartmentPills roots={roots} value={department} onChange={(v) => { setDepartment(v); setCategoryId(''); }} required label="Department" />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <label className={labelClass}>Category</label>
              <Select
                value={categoryId}
                onChange={setCategoryId}
                disabled={!department}
                placeholder="Whole department"
                options={[{ value: '', label: 'Whole department' }, ...children.map(c => ({ value: c.id, label: c.name }))]}
                buttonClassName="px-4 py-2.5 rounded-xl"
              />
            </div>
            <div>
              <label className={labelClass}>Copy from</label>
              <Select
                value={month}
                onChange={setMonth}
                disabled={months.length === 0}
                placeholder={months.length === 0 ? 'No month entered yet' : 'Pick a month'}
                options={months.map(m => ({ value: m.month, label: `${m.label} · ${m.count} ${m.count === 1 ? 'entry' : 'entries'} · ${formatCurrency(m.total)}` }))}
                buttonClassName="px-4 py-2.5 rounded-xl"
              />
            </div>
            <div>
              <label className={labelClass}>Save them on</label>
              <DatePicker value={targetDate} onChange={setTargetDate} max={todayISO()} />
            </div>
          </div>

          {loadingRows ? (
            <p className="text-sm text-gray-500 py-6 text-center">Loading the month...</p>
          ) : rows.length === 0 ? (
            <p className="text-sm text-gray-400 py-6 text-center">
              {month ? 'Nothing to copy in that month.' : 'Pick a department and a month.'}
            </p>
          ) : (
            <div className="rounded-xl border border-gray-200 overflow-hidden">
              <table className="w-full text-sm">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-3 py-2 w-10">
                      <input
                        type="checkbox"
                        aria-label="Tick every line"
                        checked={allChecked}
                        onChange={(e) => setRows(list => list.map(r => ({ ...r, checked: e.target.checked })))}
                        className="h-4 w-4 rounded border-gray-300 text-emerald-600 focus:ring-emerald-500"
                      />
                    </th>
                    <th className="px-3 py-2 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Line</th>
                    <th className="px-3 py-2 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Category</th>
                    <th className="px-3 py-2 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Was</th>
                    <th className="px-3 py-2 text-right text-xs font-semibold text-gray-500 uppercase tracking-wider w-40">Amount now</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {rows.map(r => (
                    <tr key={r.key} className={r.checked ? '' : 'text-gray-400'}>
                      <td className="px-3 py-2">
                        <input
                          type="checkbox"
                          aria-label={`Copy ${r.description}`}
                          checked={r.checked}
                          onChange={(e) => setRow(r.key, { checked: e.target.checked })}
                          className="h-4 w-4 rounded border-gray-300 text-emerald-600 focus:ring-emerald-500"
                        />
                      </td>
                      <td className="px-3 py-2 font-medium">
                        {r.description}
                        {r.paymentMode !== 'CASH' && (
                          <span className="ml-2 text-[11px] text-gray-400">{paymentLabel(r.paymentMode)}, copied as cash</span>
                        )}
                      </td>
                      <td className="px-3 py-2 text-xs">{r.categoryPath}</td>
                      <td className="px-3 py-2 text-xs whitespace-nowrap">{formatDate(r.date)}</td>
                      <td className="px-3 py-2">
                        <input
                          type="text"
                          inputMode="numeric"
                          aria-label={`Amount for ${r.description}`}
                          value={groupDigits(r.amount)}
                          onChange={(e) => handleAmountInput(e, r.amount, (d) => setRow(r.key, { amount: d }))}
                          disabled={!r.checked}
                          className={`${inputClass} py-1.5 text-right font-semibold disabled:bg-gray-50`}
                        />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        <div className="flex-shrink-0 px-6 py-4 border-t border-gray-100 bg-gray-50 flex items-center justify-between gap-3 flex-wrap">
          <p className="text-sm text-gray-600">
            {picked.length} {picked.length === 1 ? 'line' : 'lines'} ticked · <span className="font-semibold text-gray-900">{formatCurrency(total)}</span>
          </p>
          <div className="flex items-center gap-3">
            <button type="button" onClick={onClose} className="px-5 py-2.5 text-gray-700 bg-white border border-gray-200 rounded-lg hover:bg-gray-100 transition-colors font-medium">
              Cancel
            </button>
            <button
              type="button"
              onClick={save}
              disabled={saving || picked.length === 0}
              className="px-5 py-2.5 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 transition-colors font-medium disabled:opacity-50 flex items-center gap-2"
            >
              {saving && <RefreshCw className="h-4 w-4 animate-spin" />}
              Save {picked.length > 0 ? picked.length : ''} {picked.length === 1 ? 'entry' : 'entries'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CopyMonthModal;
